import { useCallback, useContext, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import useStockCodeUrl from '@hooks/useStockCodeUrl';
import { StockTickerTabContext } from '.';

const MAX_TICKER = 20;

function useStockTicker() {
  const { ticks, setTicks, isReady } = useContext(StockTickerTabContext);
  const currentCode = useStockCodeUrl();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setTicks((prev) => {
      let isExists = false;

      const appliedMaxPrev = prev.slice(MAX_TICKER * -1);

      const updatedWithActive = appliedMaxPrev.map((tick) => {
        const isActive = tick.code === currentCode;
        if (isActive) {
          isExists = true;
        }

        return { ...tick, isActive };
      });

      if (!isExists && currentCode) {
        return [...updatedWithActive, { code: currentCode, isActive: true }];
      }

      return updatedWithActive;
    });
  }, [setTicks, currentCode, isReady]);

  const closeCode = useCallback(
    /**
     * @param {string} code
     */
    (code) => {
      if (!isReady) {
        return;
      }

      const removedIx = ticks.findIndex((tick) => tick.code === code);

      setTicks((prev) => prev.filter((tick) => tick.code !== code));

      // No need to navigate
      if (code !== currentCode) {
        return;
      }

      // Need to confirm if user has 1 tab open behaviour
      if (ticks.length === 1) {
        navigate('/home');
      }

      if (removedIx === ticks.length - 1) {
        // Most right position
        navigate(`/home/saham/${ticks[removedIx - 1]?.code}`);
      }

      navigate(`/home/saham/${ticks[removedIx + 1]?.code}`);
    },
    [ticks, currentCode, isReady]
  );

  const openCode = useCallback(
    /**
     * @param {string} code
     */
    (code) => {
      if (!isReady) {
        return;
      }
      navigate(`/home/saham/${code}`);
    },
    [navigate, isReady]
  );

  return { items: ticks, closeCode, currentCode, openCode };
}

export default useStockTicker;
